/**
 * Landing-state curation for the public approvals demo (SKE-4166).
 *
 * The seeded demo account holds whatever content the demo data seeder put
 * there; the *landing state* a visitor should see is a specific, deliberate
 * mix — mostly pending, with just enough already-actioned content that the
 * board reads as a real, in-flight approval queue rather than an empty inbox.
 *
 * Everything here is a pure function over the raw `/posts` payload, so it is
 * cheap to tweak and cheap to test. The two knobs worth touching are
 * DEMO_CURATION and the slot constants below.
 */

import { v4 as uuidv4 } from 'uuid'

import { demoStrings } from './demoStrings'
import type {
  DemoPostStatus,
  DemoRawComment,
  DemoRawPost,
  DemoSettingsStatuses,
  DemoStatusType,
} from './demoTypes'

export interface DemoCuration {
  /** How many posts the visitor still has to action. */
  pending: number
  /** How many arrive already approved. */
  approved: number
  /** How many arrive with changes requested. */
  requestChange: number
}

/** ~5–8 pending + 1–2 approved + 1 changes-requested. */
export const DEMO_CURATION: DemoCuration = {
  pending: 6,
  approved: 2,
  requestChange: 1,
}

/**
 * Where the non-pending posts sit in the list.
 *
 * Fixed (never random) so every visitor sees the same landing state and
 * screenshots / Playwright runs stay stable. Index 0 is deliberately left
 * pending: the visitor's very first click should be an approval.
 */
const APPROVED_SLOTS = [2, 5]
const REQUEST_CHANGE_SLOTS = [4]

const toTime = (post: DemoRawPost): number => {
  const when = post.when ? Date.parse(post.when) : Number.NaN
  return Number.isNaN(when) ? Number.POSITIVE_INFINITY : when
}

/**
 * Build the status plan: an array of status types, one per curated post.
 * Slots that fall outside the list (or run out) spill onto the next free index
 * from the end, so shrinking the source data degrades gracefully.
 */
const buildStatusPlan = (
  total: number,
  curation: DemoCuration
): DemoStatusType[] => {
  const plan: DemoStatusType[] = Array.from({ length: total }, () => 'pending')

  const place = (slots: number[], count: number, status: DemoStatusType) => {
    let placed = 0

    for (const slot of slots) {
      if (placed >= count) {
        break
      }
      if (slot < total && plan[slot] === 'pending') {
        plan[slot] = status
        placed += 1
      }
    }

    // Spill: fill from the end of the list for anything that didn't fit.
    for (let index = total - 1; index >= 0 && placed < count; index -= 1) {
      if (plan[index] === 'pending') {
        plan[index] = status
        placed += 1
      }
    }
  }

  place(REQUEST_CHANGE_SLOTS, curation.requestChange, 'requestChange')
  place(APPROVED_SLOTS, curation.approved, 'approved')

  return plan
}

/**
 * Map a `DemoStatusType` + the link's `settings.statuses` wire shape onto a
 * normalized `DemoPostStatus`. Shared by curation and the client-only mutations
 * (demoApi.ts) so the status-object shape lives in exactly one place.
 *
 * If the settings are missing that status (they shouldn't be) keep the caller's
 * `fallback` (a post's own status) rather than inventing a broken key; with no
 * fallback, synthesize a minimal status from the type.
 */
export const buildDemoStatus = (
  statuses: DemoSettingsStatuses,
  type: DemoStatusType,
  fallback?: DemoPostStatus
): DemoPostStatus => {
  const configured = statuses?.[type]

  if (!configured?.key) {
    return fallback ?? { key: type, type, label: '' }
  }

  return {
    key: configured.key,
    type,
    label: configured.description ?? fallback?.label ?? '',
    color: configured.color,
  }
}

/**
 * Build a `StatusChanged` timeline entry. The demo synthesizes these in three
 * places (seeded history, an approve/reject action, a reply); centralizing the
 * wire shape here keeps all three in lockstep. Comment threads are entirely
 * client-side — the real link's comments are never read (they can carry real
 * customers' names, and this page is public).
 */
export const makeStatusComment = (input: {
  createdAt: string
  text?: string
  oldStatus?: DemoPostStatus | null
  newStatus?: DemoPostStatus | null
  id?: string
}): DemoRawComment => ({
  id: input.id ?? uuidv4(),
  type: 'StatusChanged',
  text: input.text ?? '',
  createdAt: input.createdAt,
  createdBy: { id: null, name: demoStrings.reviewerName },
  isMessageOwner: true,
  visibility: 'EXTERNAL',
  oldStatus: input.oldStatus ?? null,
  newStatus: input.newStatus ?? null,
})

/**
 * Marker on every comment this demo writes on the marketer's behalf
 * (SKE-4166 phase 5). Two jobs: it identifies the transient "typing…"
 * placeholder so a snapshot can never be left with a stale one, and it makes a
 * synthesised reply obvious to anything reading the store later.
 */
export const DEMO_MARKETER_METADATA_KEY = 'demoMarketerReply'

/**
 * Build the marketer's side of the conversation — a `UserWritten` note from
 * someone who is *not* the visitor, which is what makes it render as a reply
 * rather than as another of their own entries.
 *
 * `isMessageOwner: false` is the load-bearing field: `normalizeMessages` maps
 * it onto `author.isSelf`, and the timeline groups by author, so getting it
 * wrong would fold the marketer's words into the visitor's own bubble.
 * `visibility: 'EXTERNAL'` is what the approver side is allowed to see — the
 * demo forces `isExternalNotesVisible` on for exactly this reason (see
 * `applyDemoSettingsOverrides`).
 */
export const makeMarketerComment = (input: {
  text: string
  createdAt: string
  authorName: string
  id?: string
  isTyping?: boolean
}): DemoRawComment => ({
  id: input.id ?? uuidv4(),
  type: 'UserWritten',
  text: input.text,
  createdAt: input.createdAt,
  createdBy: { id: 'demo-marketer', name: input.authorName },
  isMessageOwner: false,
  visibility: 'EXTERNAL',
  oldStatus: null,
  newStatus: null,
  metadata: {
    [DEMO_MARKETER_METADATA_KEY]: true,
    ...(input.isTyping ? { demoTyping: true } : {}),
  },
})

/** Is this the transient "typing…" placeholder rather than a real reply? */
export const isDemoTypingComment = (comment: DemoRawComment): boolean =>
  comment?.metadata?.demoTyping === true

/**
 * Shape the raw `/posts` payload into the demo's landing state.
 *
 * - drafts are dropped (the demo is a scheduled-content queue, and read-only
 *   links hide drafts anyway),
 * - posts without a usable `when` are dropped (they'd break date grouping),
 * - the soonest `DEMO_CURATION` total posts are kept, oldest first,
 * - statuses are reassigned per the plan above.
 */
export const curateDemoPosts = (
  rawPosts: DemoRawPost[],
  statuses: DemoSettingsStatuses,
  curation: DemoCuration = DEMO_CURATION
): DemoRawPost[] => {
  const total = curation.pending + curation.approved + curation.requestChange

  const candidates = (Array.isArray(rawPosts) ? rawPosts : [])
    .filter((post) => post && !post.isDraft && Number.isFinite(toTime(post)))
    .sort((a, b) => toTime(a) - toTime(b))
    .slice(0, total)

  const plan = buildStatusPlan(candidates.length, curation)

  return candidates.map((post, index) => ({
    ...post,
    status: buildDemoStatus(statuses, plan[index], post.status),
  }))
}

/**
 * Seed the conversation timeline so the curated non-pending posts have the
 * history a real board would have.
 *
 * Comment threads are entirely client-side in the demo — we never read the real
 * link's comments, because those can contain real customers' names and notes
 * and this page is public.
 */
export const buildInitialDemoComments = (
  curatedPosts: DemoRawPost[],
  statuses: DemoSettingsStatuses
): Record<string, DemoRawComment[]> => {
  const comments: Record<string, DemoRawComment[]> = {}
  const pendingStatus = statuses?.pending?.key
    ? buildDemoStatus(statuses, 'pending')
    : null

  for (const post of curatedPosts) {
    if (post.status.type === 'pending') {
      continue
    }

    comments[post.id] = [
      makeStatusComment({
        text:
          post.status.type === 'requestChange'
            ? demoStrings.seededRequestChangeNote
            : '',
        // Anchored to the post's own schedule so the timeline reads in order
        // without depending on when the visitor happened to arrive.
        createdAt: new Date(toTime(post) - 60 * 60 * 1000).toISOString(),
        oldStatus: pendingStatus,
        newStatus: post.status,
      }),
    ]
  }

  return comments
}
